/**
 * Shared workspace-integration status + CRM-of-record, used by both the Integrations page (grid badges)
 * and the docked IntegrationsContextPanel. A single module-level source of truth so a connect/disconnect
 * in the panel refreshes the page grid reactively. Personal-integration status stays page-local.
 */
import { ref } from 'vue'
import { apiFetch } from '../../services/api'

export interface IntegrationStatusRow {
  provider: string
  configured?: boolean
  connected_email?: string | null
  connected_name?: string | null
  [k: string]: unknown
}

const statusByProvider = ref<Record<string, IntegrationStatusRow>>({})
const configuredByProvider = ref<Record<string, boolean>>({})
const crmOfRecord = ref('')
const transferEnabled = ref(false)
/** Set to a provider id when it just connected, so the page can flash a success banner. */
const justConnectedProvider = ref<string | null>(null)

async function refresh(): Promise<void> {
  const [integrations, ownership] = await Promise.allSettled([
    apiFetch('/settings/integrations'),
    apiFetch('/settings/ownership_settings'),
  ])
  if (integrations.status === 'fulfilled') {
    const resp = integrations.value as { integrations_export_enabled?: boolean; integrations?: IntegrationStatusRow[] }
    transferEnabled.value = Boolean(resp?.integrations_export_enabled)
    const cfg: Record<string, boolean> = {}
    const stat: Record<string, IntegrationStatusRow> = {}
    for (const row of resp?.integrations || []) {
      cfg[row.provider] = Boolean(row.configured)
      stat[row.provider] = row
    }
    configuredByProvider.value = cfg
    statusByProvider.value = stat
  }
  if (ownership.status === 'fulfilled') {
    crmOfRecord.value = (ownership.value as { crm_of_record?: string })?.crm_of_record || ''
  }
}

export function useIntegrationsStatus() {
  return { statusByProvider, configuredByProvider, crmOfRecord, transferEnabled, justConnectedProvider, refresh }
}
